// MaturaLinearExtremeInequalityOpenTask.js
// Zadanie otwarte: nierówność liniowa z ułamkiem i skrajną liczbą całkowitą.
//
// Struktura kanoniczna (do obliczeń):
//   P - (Q - R x)/D  ⧣  T x,  gdzie ⧣ to ≥ lub ≤.
// Po przekształceniu dostajemy warunek x ≤ k albo x ≥ k,
// a uczeń:
//  - w kroku 1 podaje ROZWIĄZANIE nierówności jako przedział,
//  - w kroku 2 podaje najmniejszą / największą liczbę całkowitą spełniającą nierówność.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import {
  parseRootFractionInput,
} from "../../../../components/inputs/RootFractionInput";

import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";

/* ===== helpers ===== */

const tol = 0.01 + 1e-9;
const eq = (x, y) => Math.abs(x - y) <= tol;

function normalizeUserNumberText(txt) {
  return String(txt ?? "")
    .replace(/[−–—]/g, "-")
    .replace(/\u00A0|\u202F|\u2009|\u200A|\u200B/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function parseNumberFromAny(v) {
  if (v == null) return NaN;

  if (typeof v === "object") {
    if (Number.isFinite(v.parsed)) return v.parsed;
    if (typeof v.raw === "string") {
      const s = normalizeUserNumberText(v.raw).replace(",", ".");
      return parseRootFractionInput(s);
    }
  }

  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const s = normalizeUserNumberText(v).replace(",", ".");
    return parseRootFractionInput(s);
  }

  return NaN;
}

const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a || 1;
};

function simplifyFrac(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

// ładny zapis ułamka z minusem przed całością
function fracLatex(num, den) {
  const { num: N0, den: D } = simplifyFrac(num, den);

  if (D === 1) return String(N0);
  if (N0 === 0) return "0";

  const sign = N0 < 0 ? "-" : "";
  const N = Math.abs(N0);

  return `${sign}\\dfrac{${N}}{${D}}`;
}

// zapis liczby jako liczba mieszana (jeśli się da)
function mixedFracLatex(num, den) {
  const { num: N0, den: D } = simplifyFrac(num, den);

  if (D === 1) {
    // już jest liczbą całkowitą
    return String(N0);
  }

  const sign = N0 < 0 ? "-" : "";
  const N = Math.abs(N0);
  const whole = Math.floor(N / D);
  const rest = N % D;

  // jeśli nie ma części całkowitej albo reszta = 0 – wracamy do zwykłego ułamka
  if (whole === 0 || rest === 0) {
    return `${sign}\\dfrac{${N}}{${D}}`;
  }

  // np. 26/3 -> 8 2/3
  return `${sign}${whole}\\dfrac{${rest}}{${D}}`;
}


const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

/* ===== generator ===== */

export function generateTask() {
  // kanonicznie liczymy z nierówności:
  //   P - (Q - R x)/D  ⧣  T x,  gdzie ⧣ ∈ {≥, ≤}
  // po przekształceniu:
  //   D P - (Q - R x) ⧣ D T x
  //   D P - Q + R x   ⧣ D T x
  //   D P - Q        ⧣ (D T - R) x
  // stąd w zależności od znaku (D T - R) oraz ⧣ dostajemy x ≤ k albo x ≥ k.

  while (true) {
    const D = pick([2, 3, 4]);
    const P = pick([4, 5, 6, 7, 8, 9, 10]);
    const Q = pick([0, 1, 2, 3, 4, 5, 6, 7, 8]);
    const R = pick([1, 2, 3, 4, 5, 6]);
    const T = pick([1, 2, 3, 4, 5]);

    // znak w kanonicznej postaci: "ge" (≥) lub "le" (≤)
    const opKind = pick(["ge", "le"]);

    const denomRaw = D * T - R;
    if (denomRaw === 0) continue;

    const numRaw = D * P - Q;
    const { num, den } = simplifyFrac(numRaw, denomRaw);
    const kVal = num / den;

    // sensowne wartości
    if (!Number.isFinite(kVal)) continue;
    if (Math.abs(kVal) > 20) continue;
    if (den > 9) continue;

    // nie chcemy, żeby k było całkowite
    if (Number.isInteger(kVal)) continue;

    // czy ostatecznie mamy x ≤ k (finalLess = true) czy x ≥ k
    // ge:  DP - Q ≥ (DT - R)x  →  x ≤ k dla denom>0,  x ≥ k dla denom<0
    // le:  DP - Q ≤ (DT - R)x  →  x ≥ k dla denom>0,  x ≤ k dla denom<0
    const denomPos = denomRaw > 0;
    const finalLess =
      (opKind === "ge" && denomPos) ||
      (opKind === "le" && !denomPos);

    const inclusive = true; // używamy tylko ≥ / ≤

    // typ: szukamy max całkowitej przy x ≤ k, min przy x ≥ k
    const direction = finalLess ? "max" : "min";

    // poprawna liczba całkowita
    const bestInteger =
      direction === "max" ? Math.floor(kVal) : Math.ceil(kVal);

    if (!Number.isFinite(bestInteger)) continue;
    if (Math.abs(bestInteger) > 20) continue;

    // losujemy, po której stronie stoi "brzydka" lewa część
    const swapSides = Math.random() < 0.5;

    const opLatex = opKind === "ge" ? "\\ge" : "\\le";
    const opLatexSwapped = opKind === "ge" ? "\\le" : "\\ge";

    let inequalityLatex;
    if (!swapSides) {
      // P - (Q - R x)/D ⧣ T x
      inequalityLatex = `${P} - \\dfrac{${Q} - ${R}x}{${D}} ${opLatex} ${T}x`;
    } else {
      // T x ⧣' P - (Q - R x)/D
      inequalityLatex = `${T}x ${opLatexSwapped} ${P} - \\dfrac{${Q} - ${R}x}{${D}}`;
    }

    const maxPoints = 2;

    return {
      type: "MaturaLinearExtremeInequalityOpenTask",
      data: {
        P,
        Q,
        R,
        D,
        T,
        opKind,
        denomRaw,
        k_num: num,
        k_den: den,
        k_val: kVal,
        finalLess,     // true: x ≤ k ; false: x ≥ k
        inclusive,     // tu zawsze true
        direction,     // "max" / "min"
        bestInteger,
        inequalityLatex,
        maxPoints,
      },
    };
  }
}

/* ===== treść zadania ===== */

export function renderTask({ data }) {
  const { inequalityLatex, direction, maxPoints } = data;

  const which =
    direction === "max"
      ? "największą liczbę całkowitą"
      : "najmniejszą liczbę całkowitą";

  return (
    <div style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – otwarte (0–{maxPoints}).</strong>
      </p>
      <p style={{ marginTop: 8 }}>Dana jest nierówność</p>
      <p style={{ marginTop: 4, marginBottom: 4 }}>
        <InlineMath math={inequalityLatex} />.
      </p>
      <p style={{ marginTop: 8 }}>
        Rozwiąż tę nierówność. Następnie wyznacz {which},{" "}
        która spełnia tę nierówność.
      </p>
      <p style={{ marginTop: 8 }}>
        Zapisz obliczenia. Możesz skorzystać z poniższych kroków.
      </p>
    </div>
  );
}

/* ===== input użytkownika (2 kroki) ===== */

export function renderInput(task, userInput = {}, onChange, disabled) {
  const { direction } = task.data;

  const nVal = userInput.n;
  const SVal = userInput.S;

  const askText =
    direction === "max"
      ? "Największa liczba całkowita spełniająca nierówność:"
      : "Najmniejsza liczba całkowita spełniająca nierówność:";

  return (
    <div className="flex flex-col gap-4">
      {/* Krok 1: rozwiązanie nierówności jako przedział */}
      <div className="flex flex-col gap-2">
        <strong>Krok 1.</strong>
        <div>Rozwiązanie nierówności:</div>

        {/* identyczny układ jak w prostych nierównościach liniowych */}
        <div className="flex flex-col gap-1" style={{ marginBottom: "0.6rem" }}>
          <div style={{ lineHeight: 1 }}>
            <InlineMath math={"x\\;\\in"} />
          </div>
          <IntervalInput
            value={SVal || ""}
            onChange={(val) => onChange({ ...userInput, S: val })}
            disabled={disabled}
            asString
            previewWithX
            placeholder="np. (-∞, 3/2]  lub  [1/4, ∞)"
          />
        </div>
      </div>

      {/* Krok 2: skrajna liczba całkowita */}
      <div className="flex flex-col gap-2">
        <strong>Krok 2.</strong>
        <div>{askText}</div>
        <div className="flex items-center gap-2 flex-wrap">
          <span>
            <InlineMath math={"x\\;="} />
          </span>
          <FractionInput
            value={nVal || ""}
            onChange={(val) => onChange({ ...userInput, n: val })}
            disabled={disabled}
            label="x"
            placeholder="liczba całkowita"
          />
        </div>
      </div>
    </div>
  );
}

/* ===== sprawdzanie + punktacja częściowa (0–2 pkt) ===== */

export function checkAnswer({ data }, userInput = {}) {
  const { k_val, finalLess, inclusive, bestInteger, maxPoints } = data;

  // --- krok 1: przedział (rozwiązanie nierówności) ---
  const rawS =
    typeof userInput?.S === "string"
      ? userInput.S
      : userInput?.S?.raw || userInput?.S || "";

  let stepIntervalOk = false;
  if (rawS) {
    const parsed = parseIntervalInput(rawS);
    if (
      parsed &&
      parsed.kind === "intervals" &&
      parsed.intervals.length === 1
    ) {
      const I = parsed.intervals[0];
      const t = k_val;

      if (finalLess) {
        // (-∞, t] – bo mamy ≤
        stepIntervalOk =
          I.left === -Infinity &&
          Number.isFinite(I.right) &&
          eq(I.right, t) &&
          !!I.rightClosed === !!inclusive;
      } else {
        // [t, ∞) – bo mamy ≥
        stepIntervalOk =
          I.right === Infinity &&
          Number.isFinite(I.left) &&
          eq(I.left, t) &&
          !!I.leftClosed === !!inclusive;
      }
    }
  }

  // --- krok 2: skrajna liczba całkowita ---
  const n = parseNumberFromAny(userInput.n);
  const stepIntegerOk =
    Number.isFinite(n) && Number.isInteger(n) && n === bestInteger;

  const parts = {
    "rozwiązanie nierówności": stepIntervalOk,
    "liczba całkowita spełniająca nierówność": stepIntegerOk,
  };

  const score = (stepIntervalOk ? 1 : 0) + (stepIntegerOk ? 1 : 0);

  return {
    isCorrect: score === maxPoints,
    score,
    maxScore: maxPoints,
    parts,
  };
}

/* ===== krótka poprawna odpowiedź ===== */

export function renderAnswer({ data }) {
  const {
    k_num,
    k_den,
    finalLess,
    inclusive,
    direction,
    bestInteger,
  } = data;

  // granica w zwykłym ułamku
  const kFracLatex = fracLatex(k_num, k_den);
  // granica jako liczba mieszana (jeśli sensowna)
  const kMixedLatex = mixedFracLatex(k_num, k_den);

  // rozwiązanie nierówności jako przedział
  const intervalLatex = finalLess
    ? `x\\in (-\\infty,\\; ${kFracLatex}${inclusive ? "]" : ")"}`
    : `x\\in ${inclusive ? "[" : "("}${kFracLatex},\\; \\infty)`;

  // porównanie liczby całkowitej z granicą:
  // dla "max" mamy x ≤ k → największa liczba całkowita jest mniejsza od k
  // dla "min" mamy x ≥ k → najmniejsza liczba całkowita jest większa od k
  const comparisonLatex =
    direction === "max"
      ? `${bestInteger} < ${kMixedLatex}`
      : `${bestInteger} > ${kMixedLatex}`;

  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
      {/* tylko przedział jako rozwiązanie nierówności */}
      <div>
        <InlineMath math={intervalLatex} />
      </div>

      {/* krótka odpowiedź: x = ... + relacja z liczbą mieszaną */}
      <div style={{ marginTop: 4 }}>
        <InlineMath math={`x\\;=\\;${bestInteger}`} />
        {kMixedLatex && (
          <InlineMath math={`\\quad (${comparisonLatex})`} />
        )}
      </div>
    </div>
  );
}


/* ===== podgląd odpowiedzi użytkownika ===== */

export function renderUserAnswer(userInput = {}) {
  const rawS =
    typeof userInput?.S === "string"
      ? userInput.S
      : userInput?.S?.raw || userInput?.S || "";

  const nVal =
    typeof userInput?.n === "object" ? userInput.n?.raw : userInput?.n;

  const anyGiven = (rawS && rawS.trim()) || (nVal && String(nVal).trim());
  if (!anyGiven) {
    return <i>(brak odpowiedzi)</i>;
  }

  const intervalLatex = rawS
    ? intervalToLatex(rawS, { withX: true })
    : null;

  return (
    <div className="space-y-1" style={{ textAlign: "left" }}>
      {intervalLatex && (
        <div>
          <InlineMath math={intervalLatex} />
        </div>
      )}
      {nVal && String(nVal).trim() && (
        <div>
          <InlineMath math={`x\\;=\\;${nVal}`} />
        </div>
      )}
    </div>
  );
}

/* ===== wyjaśnienie krok po kroku ===== */

export function renderExplanation({ data }) {
  const {
    P,
    Q,
    R,
    D,
    T,
    inequalityLatex,
    opKind,
    k_num,
    k_den,
    k_val,
    finalLess,
    inclusive,
    direction,
    bestInteger,
  } = data;

  const kLatex = fracLatex(k_num, k_den);

  const relLatex = finalLess
    ? (inclusive ? `x \\le ${kLatex}` : `x < ${kLatex}`)
    : (inclusive ? `x \\ge ${kLatex}` : `x > ${kLatex}`);

  const intervalLatex = finalLess
    ? `x\\in (-\\infty,\\; ${kLatex}${inclusive ? "]" : ")"}`
    : `x\\in ${inclusive ? "[" : "("}${kLatex},\\; \\infty)`;

  const ask =
    direction === "max"
      ? "największą liczbą całkowitą spełniającą nierówność jest"
      : "najmniejszą liczbą całkowitą spełniającą nierówność jest";

  const opLatex = opKind === "ge" ? "\\ge" : "\\le";

  // w wyjaśnieniu pracujemy w kanonicznej postaci
  const canonical = `${P} - \\dfrac{${Q} - ${R}x}{${D}} ${opLatex} ${T}x`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Przepisujemy zadanie w wygodniejszej postaci
        (ułamek po lewej stronie), a następnie pozbywamy się mianownika i
        wyznaczamy <InlineMath math={"x"} />.
      </p>

      <BlockMath math={inequalityLatex} />
      <BlockMath math={canonical} />
      <BlockMath
        math={`${D}\\left(${P} - \\dfrac{${Q} - ${R}x}{${D}}\\right) ${opLatex} ${D}\\cdot ${T}x`}
      />
      <BlockMath
        math={`${D}${P} - (${Q} - ${R}x) ${opLatex} ${D}${T}x`}
      />
      <BlockMath
        math={`${D}${P} - ${Q} + ${R}x ${opLatex} ${D}${T}x`}
      />
      <BlockMath
        math={`${D}${P} - ${Q} ${opLatex} (${D}${T} - ${R})x`}
      />
      <BlockMath math={relLatex} />

      <p>
        Otrzymaliśmy nierówność równoważną, z której wynika, że rozwiązaniem
        jest przedział:
      </p>
      <BlockMath math={intervalLatex} />

      <p>
        <strong>Krok 2.</strong> Szukamy skrajnej liczby całkowitej
        spełniającej nierówność.
      </p>
      <p>
        Widzimy z przedziału rozwiązań, że {ask}{" "}
        <InlineMath math={String(bestInteger)} />. To jest liczba całkowita
        leżąca najbliżej granicy <InlineMath math={kLatex} /> po odpowiedniej
        stronie (dla{" "}
        {direction === "max" ? "mniejszych lub równych" : "większych lub równych"}{" "}
        od <InlineMath math={"x"} />).
      </p>
    </div>
  );
}
